import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "components/translations/useTranslation";
import Select from "components/common/select/Select.component";
import { FormField } from "components/common/FormField.component";
import { Button, ButtonType } from "components/common/Button.component";
import OKCancelButtons from "components/modals/OKCancelButtons";
import { lookupCostCenters, saveCostUnitNodes } from "components/modals/modal.actions";
import { MODAL_CONTENT_TAB_INDEX } from "utils/constants";
import {
    AddCostUnitProps,
    AddCostUnitNodeActionData,
    CostUnitDropDownOption,
    CostUnitStateItem,
    CostUnitType,
    SelectedCostUnitNode,
} from "types/modals";

const ANY_VALUE = -99;
const UNASSIGNED_VALUE = -1;
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
// How close to the end of the list counts as "reached the bottom", in pixels.
const SCROLL_END_THRESHOLD = 24;

// Minimal shape of the react-select instance exposed to the modal:
// the only method we call on it is .focus().
interface FocusableSelectInstance {
    focus: () => void;
}

interface CostUnitDropdownProps {
    costUnit: CostUnitStateItem;
    costUnitType: CostUnitType;
    disabled: boolean;
    autoFocus: boolean;
    tabIndex: number;
    onChange: (selected: CostUnitDropDownOption | null) => void;
    selectRef?: (instance: FocusableSelectInstance | null) => void;
}

const buildSelectedOption = (
    costUnit: CostUnitStateItem,
    unassignedLabel: string,
): CostUnitDropDownOption => {
    if (costUnit.unassigned) {
        return { value: UNASSIGNED_VALUE, label: unassignedLabel };
    }
    return {
        label: costUnit.name ?? "",
        value: costUnit.id == null ? ANY_VALUE : costUnit.id,
    };
};

const toOption = (item: Record<string, any>): CostUnitDropDownOption => ({
    label:
        (item.number > 0 || typeof item.number === "string" ? `${item.number} - ` : "") + item.name,
    value: item.number,
    color: item.active ? "black" : "lightgrey",
});

const CostUnitDropdown: React.FC<CostUnitDropdownProps> = ({
    costUnit,
    costUnitType,
    disabled,
    autoFocus,
    tabIndex,
    onChange,
    selectRef,
}) => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();
    const [options, setOptions] = useState<CostUnitDropDownOption[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Paging bookkeeping is held in refs rather than state: the scroll handler and the lookup
    // callbacks have to read the current values, not the ones captured by the render that
    // created them.
    const pageRef = useRef(1);
    const prefixRef = useRef("");
    const hasMoreRef = useRef(false);
    const inFlightRef = useRef(false);
    // Monotonic token so a superseded lookup (a stale search, a page that arrives after the user
    // typed again) cannot overwrite the options of the request that replaced it.
    const requestIdRef = useRef(0);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();

    const selectedOption = useMemo(
        () => buildSelectedOption(costUnit, translate("generic.unassigned")),
        [costUnit, translate],
    );

    // "Any" and "Unassigned" are client-side entries, so they are matched against the search term
    // here - the backend knows nothing about them. They only belong on the first page.
    const buildSpecialOptions = useCallback(
        (prefix: string): CostUnitDropDownOption[] => {
            const needle = prefix.trim().toLowerCase();
            return [
                { number: ANY_VALUE, name: translate("generic.genericAny"), active: true },
                { number: UNASSIGNED_VALUE, name: translate("generic.unassigned"), active: true },
            ]
                .filter((item) => !needle || item.name.toLowerCase().includes(needle))
                .map(toOption);
        },
        [translate],
    );

    const loadPage = useCallback(
        (prefix: string, page: number) => {
            const requestId = ++requestIdRef.current;
            inFlightRef.current = true;
            prefixRef.current = prefix;
            if (page === 1) {
                setIsLoading(true);
            }

            // Returns false when a newer request has already taken over.
            const isCurrent = (): boolean => {
                if (requestId !== requestIdRef.current) {
                    return false;
                }
                inFlightRef.current = false;
                setIsLoading(false);
                return true;
            };

            dispatch(
                lookupCostCenters(
                    {
                        prefix,
                        page,
                        rows: PAGE_SIZE,
                        usedInIndexOnly: false,
                        showInactive: true,
                    },
                    costUnit.dimension,
                    (response: { rows?: Array<Record<string, any>> }) => {
                        if (!isCurrent()) {
                            return;
                        }
                        const rows = response?.rows ?? [];
                        pageRef.current = page;
                        // A short page means the end of the list. Deriving it from the row count
                        // keeps paging working regardless of whether the response carries a
                        // usable total.
                        hasMoreRef.current = rows.length === PAGE_SIZE;
                        const fetched = rows.map(toOption);
                        setOptions((previous) =>
                            page === 1
                                ? [...buildSpecialOptions(prefix), ...fetched]
                                : [...previous, ...fetched],
                        );
                    },
                    () => {
                        if (!isCurrent()) {
                            return;
                        }
                        hasMoreRef.current = false;
                        if (page === 1) {
                            setOptions(buildSpecialOptions(prefix));
                        }
                    },
                ) as any,
            );
        },
        [dispatch, costUnit.dimension, buildSpecialOptions],
    );

    useEffect(() => {
        return () => {
            clearTimeout(debounceRef.current);
            // Discard anything still in flight once this dropdown goes away.
            requestIdRef.current += 1;
        };
    }, []);

    const handleMenuOpen = useCallback(() => {
        // react-select clears the input when the menu closes, so every open starts unfiltered.
        clearTimeout(debounceRef.current);
        hasMoreRef.current = false;
        // Drop whatever a previous search left behind rather than flashing it while page 1 loads.
        setOptions([]);
        loadPage("", 1);
    }, [loadPage]);

    // Search has to go to the backend: without it the input would only filter the single page of
    // options already loaded, hiding every cost unit past the first PAGE_SIZE.
    const handleInputChange = useCallback(
        (value: string, actionMeta: any) => {
            if (actionMeta?.action !== "input-change") {
                return;
            }
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                loadPage(value, 1);
            }, SEARCH_DEBOUNCE_MS);
        },
        [loadPage],
    );

    const handleMenuListScroll = useCallback(
        (event: React.UIEvent<HTMLDivElement>) => {
            if (inFlightRef.current || !hasMoreRef.current) {
                return;
            }
            const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
            if (scrollHeight - scrollTop - clientHeight <= SCROLL_END_THRESHOLD) {
                loadPage(prefixRef.current, pageRef.current + 1);
            }
        },
        [loadPage],
    );

    const handleChange = useCallback(
        (value: CostUnitDropDownOption | null) => {
            onChange(value);
        },
        [onChange],
    );

    return (
        <FormField id={`search${costUnitType.name}`} label={costUnitType.name || ""}>
            <Select
                isClearable
                isSearchable
                isLoading={isLoading}
                isDisabled={disabled}
                autoFocus={autoFocus}
                onBlurResetsInput={false}
                onCloseResetsInput={false}
                value={selectedOption}
                placeholder=""
                showNoOptions={false}
                menuPlacement="bottom"
                menuPosition="fixed"
                menuPortalTarget={document.body}
                tabIndex={tabIndex}
                options={options}
                tabSelectsValue={false}
                // The backend already applied the search term; filtering again on the client
                // would only be able to hide rows it returned.
                filterOption={null}
                onMenuOpen={handleMenuOpen}
                onInputChange={handleInputChange}
                onMenuListScroll={handleMenuListScroll}
                onChange={handleChange as any}
                closeMenuOnSelect
                translate={translate}
                ref={selectRef}
            />
        </FormField>
    );
};

const buildInitialState = (
    costUnits: CostUnitType[],
    selectedNodes: Record<number, SelectedCostUnitNode | undefined>,
): CostUnitStateItem[] => {
    if (!costUnits?.length || selectedNodes == null) {
        return [];
    }

    return costUnits.map((costUnitType, index) => {
        const selectedNode = selectedNodes[index];
        const item: CostUnitStateItem = {
            dimension: costUnitType.dimension,
            unassigned: selectedNode == null,
        };
        if (selectedNode != null) {
            item.id = selectedNode.id != null ? selectedNode.id : undefined;
            item.name = selectedNode.name;
        }
        return item;
    });
};

const AddCostUnit = ({
    more,
    closeModal,
    loopFocus,
    errorCallback,
}: AddCostUnitProps): React.ReactElement => {
    const { translate } = useTranslation();
    const dispatch = useDispatch();

    const { costUnits, selectedNodes, companyId, treeVersion, hideClose, handleAction, getApproversTreeRoot } = more;

    const [mappedValue, setMappedValue] = useState<CostUnitStateItem[]>(() =>
        buildInitialState(costUnits, selectedNodes),
    );
    const [actionRunning, setActionRunning] = useState(false);

    const firstFocusRef = useRef<FocusableSelectInstance | null>(null);

    useEffect(() => {
        const onlyOneNode =
            selectedNodes != null &&
            Object.keys(selectedNodes).length === 1 &&
            selectedNodes[0]?.id === null;

        if (onlyOneNode) {
            const timer = setTimeout(() => {
                if (firstFocusRef.current) {
                    firstFocusRef.current.focus();
                }
            }, 10);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [selectedNodes]);

    const changeValue = useCallback(
        (selected: CostUnitDropDownOption | null, costUnit: CostUnitStateItem) => {
            setMappedValue((prev) => {
                const findIndex = prev.findIndex((unit) => unit.dimension === costUnit.dimension);
                const next = prev.map((entry) => ({ ...entry }));
                if (selected == null || selected.value === UNASSIGNED_VALUE) {
                    next[findIndex].unassigned = true;
                    next[findIndex].id = undefined;
                    next[findIndex].name = translate("generic.unassigned");
                } else {
                    next[findIndex].unassigned = false;
                    next[findIndex].name = selected.label;
                    next[findIndex].id =
                        selected.value === ANY_VALUE ? undefined : `${selected.value}`;
                }
                return next;
            });
        },
        [translate],
    );

    const handleSaveSuccess = useCallback(
        (response: unknown) => {
            closeModal();
            handleAction(response);
        },
        [closeModal, handleAction],
    );

    const handleSaveError = useCallback(
        (error: Record<string, any>) => {
            setActionRunning(false);
            const raw = error?.error;
            if (raw?.errorcode === 409) {
                errorCallback({
                    ...error,
                    error: { ...raw, errorMessages: translate("costUnitApprovers.wrongVersion") },
                });
                return;
            }
            errorCallback(error);
        },
        [errorCallback, translate],
    );

    const handleOk = useCallback(() => {
        const payload: Record<number, string | null> = {};
        mappedValue.forEach((values) => {
            if (!values.unassigned) {
                payload[values.dimension] = values.id != null ? values.id : null;
            }
        });

        const data: AddCostUnitNodeActionData = {
            companyId,
            nodes: { costUnits: payload },
            treeVersion,
            getApproversTreeRoot,
        };

        setActionRunning(true);
        dispatch(saveCostUnitNodes(data, handleSaveSuccess, handleSaveError) as any);
    }, [
        mappedValue,
        companyId,
        treeVersion,
        getApproversTreeRoot,
        dispatch,
        handleSaveSuccess,
        handleSaveError,
    ]);

    return (
        <>
            <div className="ga-modal__content add-cost-unit-combination__body overflow-auto">
                {mappedValue.map((costUnit, index) => {
                    const canEdit = index > 0 ? !mappedValue[index - 1].unassigned : true;
                    const costUnitType = costUnits[index];
                    return (
                        <CostUnitDropdown
                            key={`${costUnit.dimension}-${index}`}
                            costUnit={costUnit}
                            costUnitType={costUnitType}
                            disabled={!canEdit}
                            autoFocus={canEdit && index === 0}
                            tabIndex={MODAL_CONTENT_TAB_INDEX + index}
                            onChange={(value) => changeValue(value, costUnit)}
                            selectRef={
                                index === 0
                                    ? (element) => {
                                          firstFocusRef.current = element;
                                      }
                                    : undefined
                            }
                        />
                    );
                })}
            </div>
            {hideClose ? (
                <div className="ga-modal__actions">
                    <Button
                        type={ButtonType.PRIMARY}
                        onClick={handleOk}
                        onBlur={loopFocus}
                        tabIndex={MODAL_CONTENT_TAB_INDEX + mappedValue.length}
                        disabled={actionRunning}
                        text={translate("popUp.ok")}
                    />
                </div>
            ) : (
                <OKCancelButtons
                    tabIndex={MODAL_CONTENT_TAB_INDEX + mappedValue.length}
                    okEnabled={!actionRunning}
                    okCallback={handleOk}
                    okText={translate("popUp.ok")}
                    cancelEnabled
                    cancelCallback={closeModal}
                    cancelText={translate("popUp.cancel")}
                    loopFocus={loopFocus}
                />
            )}
        </>
    );
};

export default AddCostUnit;
